import { ref, computed, nextTick } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { useSurveyStore } from "@stores/survey/survey";
import { useResultsV2Store } from "./resultsV2";
import { contentSubtype } from "@/components/Survey/ResultsV2/constants";
import { useToastsStore } from "@/stores/toastsStore";
import useMyFetch from "@stores/useMyFetch";
import { captureEvent } from "@/lib/analytics";

export const useResultsV2CardsStore = defineStore("resultsV2Cards", () => {
  const { token: surveyToken } = storeToRefs(useSurveyStore());

  const cards = ref([]);
  const selectedCard = ref(null);
  const pendingNewCards = ref([]);
  const pendingDeletedIds = ref([]);
  const dirtyCards = ref({});
  const lastAddedCardId = ref(null);
  const collapsedSectionIds = ref(new Set());
  const configuringCardIds = ref(new Set());
  const openOptionSection = ref("data");

  let tempIdCounter = -1;

  const hasUnsavedChanges = computed(
    () =>
      pendingNewCards.value.length > 0 ||
      pendingDeletedIds.value.length > 0 ||
      Object.keys(dirtyCards.value).length > 0,
  );

  function cardsPath(viewToken) {
    return `/surveys/${surveyToken.value}/results_views/${viewToken}/cards`;
  }

  function resetDirtyState() {
    pendingNewCards.value = [];
    pendingDeletedIds.value = [];
    dirtyCards.value = {};
    tempIdCounter = -1;
  }

  function resetForView() {
    cards.value = [];
    selectedCard.value = null;
    collapsedSectionIds.value = new Set();
    configuringCardIds.value = new Set();
    resetDirtyState();
  }

  // Section cards in display order — the spine of the view's outline
  const sectionCards = computed(() =>
    [...cards.value]
      .filter((card) => contentSubtype(card) === "section")
      .sort((a, b) => a.position - b.position),
  );

  // Maps each card id to the id of the section card that precedes it (by position)
  const sectionIdByCardId = computed(() => {
    const map = {};
    const sorted = [...cards.value].sort((a, b) => a.position - b.position);
    let currentSectionId = null;
    for (const card of sorted) {
      if (contentSubtype(card) === "section") {
        currentSectionId = card.id;
        continue;
      }
      if (currentSectionId != null) map[card.id] = currentSectionId;
    }
    return map;
  });

  function toggleSectionCollapse(sectionId) {
    const next = new Set(collapsedSectionIds.value);
    if (next.has(sectionId)) {
      next.delete(sectionId);
    } else {
      next.add(sectionId);
    }
    collapsedSectionIds.value = next;
  }

  function isConfiguring(cardId) {
    return configuringCardIds.value.has(cardId);
  }

  function openConfig(cardId) {
    configuringCardIds.value = new Set(configuringCardIds.value).add(cardId);
  }

  function closeConfig(cardId) {
    if (!configuringCardIds.value.has(cardId)) return;
    const next = new Set(configuringCardIds.value);
    next.delete(cardId);
    configuringCardIds.value = next;
  }

  function expandSection(sectionId) {
    if (!collapsedSectionIds.value.has(sectionId)) return;
    const next = new Set(collapsedSectionIds.value);
    next.delete(sectionId);
    collapsedSectionIds.value = next;
  }

  function expandSectionForCard(cardId) {
    const sectionId = sectionIdByCardId.value[cardId];
    if (sectionId != null) expandSection(sectionId);
  }

  async function fetchCards(viewToken) {
    if (!viewToken) {
      cards.value = [];
      return [];
    }
    const { response, data } = await useMyFetch(cardsPath(viewToken)).get().json();
    if (!response.value.ok) return [];
    cards.value = data.value || [];
    return cards.value;
  }

  function setCards(list) {
    cards.value = list || [];
  }

  function computeInsertPosition(list, targetPosition) {
    if (!list.length) return 100;
    const sorted = [...list].sort((a, b) => a.position - b.position);
    const afterIndex = sorted.findIndex((c) => c.position >= targetPosition);
    if (afterIndex === -1) return (sorted.at(-1)?.position || 0) + 100;
    if (afterIndex === 0) return sorted[0].position / 2;
    return (sorted[afterIndex - 1].position + sorted[afterIndex].position) / 2;
  }

  function addCard(cardType, cardId = null, position = null) {
    const tempId = tempIdCounter--;
    const insertPosition =
      position !== null
        ? computeInsertPosition(cards.value, position)
        : (cards.value.at(-1)?.position || 0) + 100;

    const newCard = {
      id: tempId,
      card_type: cardType,
      card_id: cardId,
      position: insertPosition,
      content: {},
      options: null,
      _isNew: true,
    };

    const insertIndex = cards.value.findIndex((c) => c.position > insertPosition);
    if (insertIndex === -1) {
      cards.value.push(newCard);
    } else {
      cards.value.splice(insertIndex, 0, newCard);
    }

    pendingNewCards.value.push(newCard);
    selectedCard.value = newCard;
    lastAddedCardId.value = newCard.id;

    nextTick(() => {
      const sidebarEl = document.getElementById(`sidebar-card-${newCard.id}`);
      if (sidebarEl) sidebarEl.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    return newCard;
  }

  function updateCardLocal(cardId, changes) {
    const card = cards.value.find((c) => c.id === cardId);
    if (!card) return;
    Object.assign(card, changes);
    // View mode has no Save button: changes there are ephemeral display tweaks,
    // marking them dirty would strand unsaveable "unsaved changes".
    if (useResultsV2Store().editMode) dirtyCards.value[cardId] = true;
  }

  function removeCard(cardId) {
    const card = cards.value.find((c) => c.id === cardId);
    if (!card) return;

    cards.value = cards.value.filter((c) => c.id !== cardId);

    if (card._isNew) {
      pendingNewCards.value = pendingNewCards.value.filter((c) => c.id !== cardId);
    } else {
      pendingDeletedIds.value.push(cardId);
    }

    delete dirtyCards.value[cardId];

    if (selectedCard.value?.id === cardId) {
      selectedCard.value = null;
    }
    closeConfig(cardId);
  }

  function moveCard(cardId, newIndex) {
    const card = cards.value.find((c) => c.id === cardId);
    if (!card) return;

    const others = cards.value
      .filter((c) => c.id !== cardId)
      .sort((a, b) => a.position - b.position);

    const prev = newIndex > 0 ? others[newIndex - 1] : null;
    const next = newIndex < others.length ? others[newIndex] : null;

    let newPosition;
    if (!prev) newPosition = (next?.position ?? 100) / 2;
    else if (!next) newPosition = prev.position + 100;
    else newPosition = (prev.position + next.position) / 2;

    if (newPosition === card.position) return;

    card.position = newPosition;
    dirtyCards.value[card.id] = true;
    cards.value = [...cards.value].sort((a, b) => a.position - b.position);
  }

  function selectCard(cardId) {
    selectedCard.value = cards.value.find((c) => c.id === cardId) || null;
  }

  async function saveAllCards() {
    const viewToken = useResultsV2Store().currentView?.token;
    if (!viewToken) return;

    const basePath = cardsPath(viewToken);
    const newCardIds = new Set(pendingNewCards.value.map((c) => c.id));

    const deletions = pendingDeletedIds.value.map((id) =>
      useMyFetch(`${basePath}/${id}`).delete(),
    );

    const creations = pendingNewCards.value
      .filter((card) => !(card.card_type === "recode" && !card.card_id))
      .map((card) =>
        useMyFetch(basePath)
          .post({
            card: {
              card_type: card.card_type,
              card_id: card.card_id,
              position: card.position,
              content: card.content || {},
              options: card.options,
            },
          })
          .json(),
      );

    const updates = Object.keys(dirtyCards.value)
      .map(Number)
      .filter((id) => !newCardIds.has(id))
      .map((id) => {
        const card = cards.value.find((c) => c.id === id);
        if (!card) return null;
        return useMyFetch(`${basePath}/${id}`)
          .patch({
            card: {
              card_type: card.card_type,
              card_id: card.card_id,
              position: card.position,
              content: card.content || {},
              options: card.options,
            },
          })
          .json();
      })
      .filter(Boolean);

    const saved = await Promise.all([...deletions, ...creations, ...updates]);

    const refused = saved.filter((result) => !result?.response?.value?.ok);
    if (refused.length) {
      const reasons = [...new Set(refused.flatMap((result) => result?.data?.value?.errors || []))];
      useToastsStore().error(
        refused.length === 1 ? "A card could not be saved" : `${refused.length} cards could not be saved`,
        reasons.join(". "),
      );
    } else {
      captureEvent("results_view_saved", {
        view_token: viewToken,
        cards_created: creations.length,
        cards_updated: updates.length,
        cards_deleted: deletions.length,
      });
    }

    resetDirtyState();
  }

  async function setCardWidth(cardId, width) {
    const card = cards.value.find((c) => c.id === cardId);
    if (!card) return;
    const previousOptions = card.options;
    card.options = { ...card.options, width };

    const viewToken = useResultsV2Store().currentView?.token;
    // An unsaved card has no row yet; its pending creation carries the new width.
    if (!viewToken || cardId < 0) return;

    const { response } = await useMyFetch(`${cardsPath(viewToken)}/${cardId}`)
      .patch({ card: { options: card.options } })
      .json();

    if (!response.value?.ok) {
      card.options = previousOptions;
      useToastsStore().error("Failed to save card width");
    }
  }

  return {
    cards,
    selectedCard,
    configuringCardIds,
    isConfiguring,
    openConfig,
    closeConfig,
    openOptionSection,
    pendingNewCards,
    pendingDeletedIds,
    dirtyCards,
    hasUnsavedChanges,
    lastAddedCardId,
    collapsedSectionIds,
    sectionCards,
    sectionIdByCardId,
    toggleSectionCollapse,
    expandSection,
    expandSectionForCard,
    resetDirtyState,
    resetForView,
    fetchCards,
    setCards,
    addCard,
    updateCardLocal,
    removeCard,
    moveCard,
    selectCard,
    saveAllCards,
    setCardWidth,
  };
});
